import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider, useAuth } from './AuthContext';
import AuthForm from './components/AuthForm';
import Navbar from './components/Navbar/Navbar';
import TaskDashboard from './components/TaskDashboard';
import CustomerDashboard from './components/CustomerDashboard';
import CustomerDetail from './components/CustomerDetail/CustomerDetail';
import ProjectDashboard from './components/ProjectDashboard';
import ProjectDetail from './components/ProjectDetail/ProjectDetail';
import AutomationManager from './components/AutomationManager/AutomationManager';
import AdminPage from './components/AdminPage/AdminPage';
import UserActivityPage from './components/UserActivityPage/UserActivityPage';
import EmailTemplates from './components/EmailTemplates/EmailTemplates';
import DocumentTemplates from './components/DocumentTemplates/DocumentTemplates';
import SmsInbox from './components/Messages/SmsInbox';
import BoardsList from './components/Boards/BoardsList';
import BoardView from './components/Boards/BoardView';
import Settings from './components/Settings/Settings';
import CrewsPage from './components/Crews/CrewsPage';
import LeadSources from './components/LeadSources/LeadSources';
import CustomFields from './components/CustomFields/CustomFields';
import './App.css';

// Read ?section=... and optional ?projectId=... from the URL on first render.
// Used by OAuth redirects (e.g. /?section=settings&gmail=connected) and by
// links in automated emails (e.g. /?section=project-detail&projectId=42).
// Each screen's URL. A list and its detail view share a slug and are told apart by
// whether an id follows it, so /projects is the list and /projects/41 is one job.
//
// `idKey` names which piece of selection state the id belongs to, so one table
// drives both directions — building a URL from state, and restoring state from a
// URL — instead of two lists that drift apart.
const ROUTES = [
  { slug: '',                   list: 'tasks' },
  { slug: 'projects',           list: 'projects',           detail: 'project-detail',  idKey: 'projectId' },
  { slug: 'contacts',           list: 'customers',          detail: 'customer-detail', idKey: 'customerId' },
  { slug: 'boards',             list: 'boards',             detail: 'board-view',      idKey: 'boardId' },
  { slug: 'team',               list: 'admin',              detail: 'user-activity',   idKey: 'userId' },
  { slug: 'messages',           list: 'messages' },
  { slug: 'crews',              list: 'crews' },
  { slug: 'automations',        list: 'automations' },
  { slug: 'email-templates',    list: 'email-templates' },
  { slug: 'document-templates', list: 'document-templates' },
  { slug: 'lead-sources',       list: 'lead-sources' },
  { slug: 'custom-fields',      list: 'custom-fields' },
  { slug: 'settings',           list: 'settings' },
];

const routeForSection = (section) =>
  ROUTES.find((r) => r.list === section || r.detail === section);

// The URL a given screen should show. Detail views carry their id; everything else
// is just its slug.
function pathForState(section, ids) {
  const route = routeForSection(section);
  if (!route) return '/';
  const id = route.detail === section && route.idKey ? ids[route.idKey] : null;
  return '/' + [route.slug, id].filter((p) => p !== null && p !== undefined && p !== '').join('/');
}

// The reverse: what a URL means. Unknown paths fall back to tasks rather than
// rendering nothing, since a stale bookmark should still open the app.
function stateFromPath(pathname) {
  const segments = (pathname || '/').split('/').filter(Boolean);
  const slug = segments[0] || '';
  const route = ROUTES.find((r) => r.slug === slug);
  if (!route) return { section: 'tasks', ids: {} };

  const rawId = segments[1];
  const id = rawId ? parseInt(rawId, 10) : null;

  if (id !== null && Number.isFinite(id) && route.detail) {
    return { section: route.detail, ids: { [route.idKey]: id } };
  }
  return { section: route.list, ids: {} };
}

function readInitialFromUrl() {
  if (typeof window === 'undefined') return { section: 'tasks', ids: {}, fromUrl: false };

  // Legacy ?section= links still work — they were handed out before paths existed,
  // and the emails already sent carry them. Translated to the new URL on arrival so
  // the address bar matches everything else from that point on.
  const params = new URLSearchParams(window.location.search);
  const legacySection = params.get('section');
  if (legacySection && routeForSection(legacySection)) {
    const projectIdStr = params.get('projectId');
    const projectId = projectIdStr ? parseInt(projectIdStr, 10) : null;
    const ids = Number.isFinite(projectId) ? { projectId } : {};
    window.history.replaceState({}, '', pathForState(legacySection, ids));
    return { section: legacySection, ids, fromUrl: true };
  }

  const fromPath = stateFromPath(window.location.pathname);
  // "/" is where the app opens by default, not a screen anyone asked for, so it
  // leaves the landing preference free to decide. Any other path is a deliberate
  // destination and wins.
  const asked = window.location.pathname !== '/' && window.location.pathname !== '';
  return { ...fromPath, fromUrl: asked };
}

// Main dashboard component that renders different sections
function Dashboard() {
  // Read once, not on every render — it calls replaceState for legacy links, and
  // re-running that mid-render is both wasteful and confusing to debug.
  const [initial] = useState(readInitialFromUrl);
  const [currentSection, setCurrentSection] = useState(initial.section);
  const [selectedProjectId, setSelectedProjectId] = useState(initial.ids.projectId ?? null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(initial.ids.customerId ?? null);
  const [selectedUserId, setSelectedUserId] = useState(initial.ids.userId ?? null);
  const [selectedBoardId, setSelectedBoardId] = useState(initial.ids.boardId ?? null);
  const { user } = useAuth();

  // Keep the address bar in step with what is on screen, and make Back work.
  //
  // Navigation is React state, and state changes leave no history entry — so Back
  // went wherever the browser was before Artemis, which read as the app dumping you
  // on the home page. Every screen change now pushes an entry, and popstate puts the
  // state back, so Back returns to the previous project instead of leaving.
  //
  // fromPopState stops the two from fighting: without it, restoring state from a
  // popstate would immediately push a new entry for the screen we just went back to,
  // and Back would never get any further.
  const fromPopState = useRef(false);
  const lastPath = useRef(null);

  useEffect(() => {
    const onPop = () => {
      const { section, ids } = stateFromPath(window.location.pathname);
      fromPopState.current = true;
      setCurrentSection(section);
      setSelectedProjectId(ids.projectId ?? null);
      setSelectedCustomerId(ids.customerId ?? null);
      setSelectedUserId(ids.userId ?? null);
      setSelectedBoardId(ids.boardId ?? null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const path = pathForState(currentSection, {
      projectId: selectedProjectId,
      customerId: selectedCustomerId,
      userId: selectedUserId,
      boardId: selectedBoardId,
    });

    if (fromPopState.current) {
      // Arrived here by Back; the URL is already correct.
      fromPopState.current = false;
      lastPath.current = path;
      return;
    }

    // Guards the case where a detail view sets its id and section in two renders:
    // the intermediate path is identical to the current one, and pushing it would
    // put a duplicate entry in history that Back appears to ignore.
    if (lastPath.current === path) return;

    // The first path replaces rather than pushes, so Back from the opening screen
    // leaves the app instead of cycling within it.
    if (lastPath.current === null) window.history.replaceState({}, '', path);
    else window.history.pushState({}, '', path);

    lastPath.current = path;
  }, [currentSection, selectedProjectId, selectedCustomerId, selectedUserId, selectedBoardId]);

  // Apply the person's landing preference once, after sign-in resolves. It cannot be
  // part of the initial state: this renders before AuthProvider has read the stored
  // session, so `user` is null on the first pass. The ref makes it a one-shot —
  // without it, any later re-render would drag someone back to their landing screen
  // mid-task.
  const landingApplied = useRef(false);
  useEffect(() => {
    if (landingApplied.current || initial.fromUrl || !user) return;
    landingApplied.current = true;
    if (user.landingSection) {
      // Replace, don't push. The landing screen is where the app opened, not
      // somewhere navigated to — pushing would mean Back from it went to Tasks,
      // a screen the person never actually visited.
      lastPath.current = null;
      setCurrentSection(user.landingSection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Check if user is admin
  const isAdmin = user?.role === "Admin";

  const navigateToProject = (id) => {
    setSelectedProjectId(id);
    setCurrentSection('project-detail');
  };

  const navigateToCustomer = (id) => {
    setSelectedCustomerId(id);
    setCurrentSection('customer-detail');
  };

  const navigateToUserActivity = (id) => {
    setSelectedUserId(id);
    setCurrentSection('user-activity');
  };

  const navigateToBoard = (id) => {
    setSelectedBoardId(id);
    setCurrentSection('board-view');
  };

  const renderSection = () => {
    switch(currentSection) {
      case 'tasks':
        return <TaskDashboard
          onNavigateToProject={navigateToProject}
          onNavigateToCustomer={navigateToCustomer}
          onNavigateToUser={navigateToUserActivity}
          onNavigateToBoards={() => setCurrentSection('boards')}
        />;
      case 'boards':
        return <BoardsList onOpenBoard={navigateToBoard} />;
      case 'board-view':
        return <BoardView
          boardId={selectedBoardId}
          onBack={() => setCurrentSection('boards')}
          onOpenEntity={(entityId, type) =>
            type === 'Contacts' ? navigateToCustomer(entityId) : navigateToProject(entityId)}
        />;
      case 'user-activity':
        return <UserActivityPage
          userId={selectedUserId}
          onBack={() => setCurrentSection('tasks')}
          onNavigateToProject={navigateToProject}
        />;
      case 'customers':
        return <CustomerDashboard onViewCustomer={navigateToCustomer} />;
      case 'projects':
        return <ProjectDashboard onViewProject={navigateToProject} />;
      case 'messages':
        return <SmsInbox />;
      case 'project-detail':
        return <ProjectDetail
          projectId={selectedProjectId}
          onBack={() => setCurrentSection('projects')}
        />;
      case 'customer-detail':
        return <CustomerDetail
          customerId={selectedCustomerId}
          onBack={() => setCurrentSection('customers')}
          onViewProject={navigateToProject}
        />;
      case 'admin':
        if (!isAdmin) {
          return (
            <div className="access-denied-section">
              <div className="denied-content">
                <div className="denied-icon">🚫</div>
                <h2>Access Denied</h2>
                <p>Only administrators can access this page.</p>
                <div className="denied-details">
                  <p>Current role: {user?.role || 'Unknown'}</p>
                  <p>Required role: Administrator</p>
                </div>
                <button
                  className="back-btn"
                  onClick={() => setCurrentSection('tasks')}
                >
                  Go to Tasks
                </button>
              </div>
            </div>
          );
        }
        return <AdminPage />;
      case 'settings':
        return <Settings />;
      case 'crews':
        return <CrewsPage />;
      case 'lead-sources':
        return <LeadSources />;
      case 'custom-fields':
        return <CustomFields />;
      case 'automations':
        // Admin-only section
        if (!isAdmin) {
          return (
            <div className="access-denied-section">
              <div className="denied-content">
                <div className="denied-icon">🚫</div>
                <h2>Access Denied</h2>
                <p>Only administrators can access automation management.</p>
                <div className="denied-details">
                  <p>Current role: {user?.role || 'Unknown'}</p>
                  <p>Required role: Administrator</p>
                </div>
                <button
                  className="back-btn"
                  onClick={() => setCurrentSection('tasks')}
                >
                  Go to Tasks
                </button>
              </div>
            </div>
          );
        }
        return (
          <div className="automation-section">
            <AutomationManager />
          </div>
        );
      case 'email-templates':
        if (!isAdmin) {
          return (
            <div className="access-denied-section">
              <div className="denied-content">
                <div className="denied-icon">🚫</div>
                <h2>Access Denied</h2>
                <p>Only administrators can manage email templates.</p>
                <button
                  className="back-btn"
                  onClick={() => setCurrentSection('tasks')}
                >
                  Go to Tasks
                </button>
              </div>
            </div>
          );
        }
        return <EmailTemplates />;
      case 'document-templates':
        if (!isAdmin) {
          return (
            <div className="access-denied-section">
              <div className="denied-content">
                <div className="denied-icon">🚫</div>
                <h2>Access Denied</h2>
                <p>Only administrators can manage document templates.</p>
                <button
                  className="back-btn"
                  onClick={() => setCurrentSection('tasks')}
                >
                  Go to Tasks
                </button>
              </div>
            </div>
          );
        }
        return <DocumentTemplates />;
      default:
        return <TaskDashboard />;
    }
  };

  return (
    <div className="app">
      <Navbar
        currentSection={currentSection}
        onSectionChange={setCurrentSection}
        onNavigateToProject={navigateToProject}
      />
      <main className="main-content">
        {renderSection()}
      </main>
    </div>
  );
}

// Auth routing component
function AppContent() {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return user ? <Dashboard /> : <AuthForm />;
}

// Root App component
function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;